import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react';
import { Table } from 'ksnm-common-ui/lib/Table2/Table';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import type { Filter as KSNMFilter } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { DesignLayout } from '@common/DesignLayout';
import { Tabs } from '@common/Tabs';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { counterpartiesStore, TABS } from '@stores/dictionaryStores/counterpartiesStore/counterpartiesStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { RoutePath } from '../../../AppRouter/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { ActionButton } from './ActionButton';
import { getColumns } from './columns';
import { Filter } from './Filter';

import styles from './Counterparties.module.scss';

const { TABLE_NAMES } = constantStore;

export const Counterparties = observer(() => {
  const { clear, tab, setTab, counterparties, setChecked, fetch, organization, setOrganization } = counterpartiesStore;

  const { push } = useHistory();

  useAuditView('Контрагенты');

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => () => clear(), [clear]);

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_COUNTERPARTIES });

  const hardFilter = useMemo(() => {
    const out: KSNMFilter[] = [
      {
        name: 'counterparty',
        type: FILTER.EQ,
        values: [tab === TABS.COMPANIES_CM],
        filterIsInverted: false,
      },
    ];

    if (organization) {
      out.push({
        name: 'org_local',
        type: FILTER.EQ,
        values: [organization],
        filterIsInverted: false,
      });
    }

    return [out];
  }, [organization, tab]);

  const localData = useMemo(
    () =>
      counterparties.map((item) =>
        objectFieldsToSnakeCase({
          ...item,
          unique_id: `${item.code ?? ''}${item.counterpartyTypeCode ?? ''}${item.orgLocal ?? ''}`,
        }),
      ),
    [counterparties],
  );

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // // TODO переделать когда, в табличном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr },
        localData: {
          data: localData,
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'unique_id',
      });

      setChecked(rows);
    },
    [localData, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(
        `${RoutePath.DICTIONARY_COUNTERPARTIES}/${value?.code}/${value?.counterparty_type_code}/${
          value?.org_local ?? ''
        }`,
      );
    },
    [push],
  );

  const title = (
    <Tabs
      tabs={[
        {
          title: 'Компании - владельцы сервиса РМ',
          key: TABS.COMPANIES_CM,
        },
        {
          title: 'Контрагенты',
          key: TABS.COUNTERPARTIES,
        },
      ]}
      onChange={(v) => {
        setTab(v);
        setChecked([]);
        setOrganization(undefined);
      }}
      active={tab}
    />
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  const columns = useMemo(() => getColumns(tab), [tab]);

  return (
    <DesignLayout>
      <Filter />

      <div className={styles.table}>
        <Table
          externalControl={externalControl}
          tableName={TABLE_NAMES.DICTIONARY_COUNTERPARTIES}
          columns={columns}
          tableTitle={titleWithCount}
          resource="cm.v_company"
          externalFetch={ksnmTableStore.externalFetch}
          externalFetchXLSX={getExcelWithAudit('Справочник "Контрагенты"')}
          hardFilter={hardFilter}
          onRowClick={onRowClick}
          onCheckBoxChange={onCheckBoxChange}
          extendTableButtons={<ActionButton update={update} />}
          showAggregateRow={false}
          key={key}
          keyField="unique_id"
          onTableChange={onTableChange}
        />
      </div>
    </DesignLayout>
  );
});
